import { z } from "zod";

const LANDING_WEBSITE_INTENT_KEY = "cheetah.landingWebsiteIntent";
const LANDING_WEBSITE_INTENT_TTL_MS = 30 * 60 * 1000;

const websiteSchema = z
  .string()
  .trim()
  .transform((value) => (!/^https?:\/\//i.test(value) ? `https://${value}` : value))
  .pipe(z.url())
  .refine((value) => new URL(value).hostname.split(".").filter(Boolean).length >= 2);

type LandingWebsiteIntent = {
  sourceUrl: string;
  expiresAt: number;
};

type LandingWebsiteIntentOptions = {
  storage?: Storage | null;
  now?: () => number;
};

const resolveStorage = (storage?: Storage | null): Storage | null => {
  if (storage !== undefined) {
    return storage;
  }

  if (typeof window === "undefined") {
    return null;
  }

  return window.sessionStorage ?? null;
};

const resolveNow = (now?: () => number): number => (now ?? Date.now)();

export const normalizeLandingWebsite = (value: string): string | null => {
  const result = websiteSchema.safeParse(value);
  return result.success ? result.data : null;
};

export const captureLandingWebsiteIntent = (
  search: string,
  options: LandingWebsiteIntentOptions = {},
): string | null => {
  const storage = resolveStorage(options.storage);
  if (!storage) {
    return null;
  }

  const website = new URLSearchParams(search).get("website");
  if (!website) {
    return null;
  }

  const sourceUrl = normalizeLandingWebsite(website);
  if (!sourceUrl) {
    return null;
  }

  storage.setItem(
    LANDING_WEBSITE_INTENT_KEY,
    JSON.stringify({
      sourceUrl,
      expiresAt: resolveNow(options.now) + LANDING_WEBSITE_INTENT_TTL_MS,
    } satisfies LandingWebsiteIntent),
  );

  return sourceUrl;
};

export const peekLandingWebsiteIntent = (
  options: LandingWebsiteIntentOptions = {},
): string | null => {
  const storage = resolveStorage(options.storage);
  if (!storage) {
    return null;
  }

  const rawIntent = storage.getItem(LANDING_WEBSITE_INTENT_KEY);
  if (!rawIntent) {
    return null;
  }

  try {
    const intent = JSON.parse(rawIntent) as Partial<LandingWebsiteIntent>;
    if (
      typeof intent.sourceUrl !== "string" ||
      typeof intent.expiresAt !== "number" ||
      intent.expiresAt <= resolveNow(options.now)
    ) {
      storage.removeItem(LANDING_WEBSITE_INTENT_KEY);
      return null;
    }

    const sourceUrl = normalizeLandingWebsite(intent.sourceUrl);
    if (!sourceUrl) {
      storage.removeItem(LANDING_WEBSITE_INTENT_KEY);
      return null;
    }

    return sourceUrl;
  } catch {
    storage.removeItem(LANDING_WEBSITE_INTENT_KEY);
    return null;
  }
};

export const consumeLandingWebsiteIntent = (
  options: LandingWebsiteIntentOptions = {},
): string | null => {
  const storage = resolveStorage(options.storage);
  const sourceUrl = peekLandingWebsiteIntent({ ...options, storage });
  storage?.removeItem(LANDING_WEBSITE_INTENT_KEY);
  return sourceUrl;
};
